import { getCurrentRateInfo } from "@/libs/amount";

export type ResolvedRateSource =
  | "contract_date"
  | "launch_date"
  | "current_fallback"
  | "eur"
  | "unavailable";

export type ResolvedRateToEuro = {
  rateToEuro: number | null;
  dateUsed: string | null;
  source: ResolvedRateSource;
};

function normalizeDateForRateApi(value: any): string | null {
  if (!value) return null;

  if (typeof value === "string") {
    const trimmed = value.trim();
    const match = trimmed.match(/^(\d{4}-\d{2}-\d{2})/);
    if (match?.[1]) return match[1];
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export function readRealFundingContractDateRaw(funding: any) {
  return (
    funding?.dateAmount ??
    funding?.date_amount ??
    funding?.dateOldRate ??
    funding?.date_old_rate ??
    null
  );
}

export function readRealFundingDateUsesFallback(funding: any): boolean {
  const raw =
    funding?.dateAmountIsFallback ??
    funding?.date_amount_is_fallback ??
    funding?.dateAmountFallback ??
    funding?.date_amount_fallback ??
    funding?.dateAmountFallbackToOldRate ??
    funding?.date_amount_fallback_to_old_rate;

  if (raw === true || raw === false) return raw;
  if (raw === 1 || raw === "1") return true;
  if (raw === 0 || raw === "0") return false;
  if (typeof raw === "string" && raw.trim()) {
    const normalized = raw.trim().toLowerCase();
    if (normalized === "true") return true;
    if (normalized === "false") return false;
  }

  const dateAmountRaw =
    funding?.dateAmountRaw ??
    funding?.date_amount_raw ??
    null;
  const dateOldRateRaw = funding?.dateOldRate ?? funding?.date_old_rate ?? null;
  if (!dateOldRateRaw) return false;
  if (!dateAmountRaw) return true;

  const normalizeDate = (value: any): string | null => {
    if (!value) return null;
    if (typeof value === "string") {
      const trimmed = value.trim();
      const match = trimmed.match(/^(\d{4}-\d{2}-\d{2})/);
      if (match?.[1]) return match[1];
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  // Quand la vraie dateAmount est renseignée, même si elle égale dateOldRate, ce n'est pas un fallback.
  const dateAmount = normalizeDate(dateAmountRaw);
  return !dateAmount;
}

export function readProjectLaunchDateRaw(project: any) {
  return project?.launchDate ?? project?.launch_date ?? null;
}

export function readProjectCurrencyCode(project: any): string {
  return String(project?.country?.currency?.code ?? "")
    .trim()
    .toUpperCase();
}

export function readProjectCurrentRateToEuro(project: any): number | null {
  const exchange = project?.country?.currency?.exchangeRates?.[0];
  const rate =
    typeof exchange?.rate === "number" && exchange.rate > 0
      ? exchange.rate
      : typeof exchange?.mid === "number" && exchange.mid > 0
        ? 1 / exchange.mid
        : null;

  return typeof rate === "number" && rate > 0 ? rate : null;
}

export function readProjectCurrentRateDate(project: any): string | null {
  return normalizeDateForRateApi(project?.country?.currency?.exchangeRates?.[0]?.ratedAt);
}

export type HistoricalRateFetchStatus =
  | "ok"
  | "eur"
  | "invalid_input"
  | "http_404"
  | "http_error"
  | "network_error";

export type HistoricalRateFetchResult = {
  rateToEuro: number | null;
  dateUsed: string | null;
  status: HistoricalRateFetchStatus;
};

const historicalRateCache = new Map<string, Promise<HistoricalRateFetchResult>>();

async function fetchHexaRateRateToEuroAtDateRaw(
  currencyCodeInput: any,
  contractDateInput: any,
): Promise<HistoricalRateFetchResult> {
  const currencyCode = String(currencyCodeInput ?? "")
    .trim()
    .toUpperCase();
  if (!currencyCode) {
    return { rateToEuro: null, dateUsed: null, status: "invalid_input" };
  }
  if (currencyCode === "EUR") {
    return { rateToEuro: 1, dateUsed: null, status: "eur" };
  }

  const contractDate = normalizeDateForRateApi(contractDateInput);
  if (!contractDate) {
    return { rateToEuro: null, dateUsed: null, status: "invalid_input" };
  }

  const cacheKey = `${currencyCode}:${contractDate}`;
  const cached = historicalRateCache.get(cacheKey);
  if (cached) return cached;

  const promise = (async () => {
    try {
      const url = `https://api.frankfurter.dev/v2/rates?base=EUR&quotes=${currencyCode}&date=${contractDate}`;
      const response = await fetch(url);
      if (!response.ok) {
        return {
          rateToEuro: null,
          dateUsed: contractDate,
          status: response.status === 404 ? "http_404" : "http_error",
        };
      }

      const data = await response.json();
      const entries = Array.isArray(data) ? data : [];
      const entry = (entries as any[]).find((e) => e.quote === currencyCode);
      const rate = Number(entry?.rate);
      if (!Number.isFinite(rate) || rate <= 0) {
        return {
          rateToEuro: null,
          dateUsed: contractDate,
          status: "http_error",
        };
      }

      return {
        rateToEuro: 1 / rate,
        dateUsed: contractDate,
        status: "ok",
      };
    } catch {
      return {
        rateToEuro: null,
        dateUsed: contractDate,
        status: "network_error",
      };
    }
  })();

  historicalRateCache.set(cacheKey, promise);
  return promise;
}

export async function resolveHistoricalRateToEuroAtDate(
  currencyCodeInput: any,
  dateInput: any,
  sourceOnSuccess: Extract<ResolvedRateSource, "contract_date" | "launch_date">,
): Promise<ResolvedRateToEuro> {
  const result = await fetchHexaRateRateToEuroAtDateRaw(currencyCodeInput, dateInput);

  if (result.status === "ok") {
    return {
      rateToEuro: result.rateToEuro,
      dateUsed: result.dateUsed,
      source: sourceOnSuccess,
    };
  }

  if (result.status === "eur") {
    return {
      rateToEuro: 1,
      dateUsed: null,
      source: "eur",
    };
  }

  return {
    rateToEuro: null,
    dateUsed: result.dateUsed,
    source: "unavailable",
  };
}

export async function fetchHexaRateRateToEuroAtDate(
  currencyCodeInput: any,
  contractDateInput: any,
): Promise<number | null> {
  const result = await fetchHexaRateRateToEuroAtDateRaw(
    currencyCodeInput,
    contractDateInput,
  );
  return result.rateToEuro;
}

export async function fetchHexaRateRateToEuroAtDateDetailed(
  currencyCodeInput: any,
  contractDateInput: any,
): Promise<HistoricalRateFetchResult> {
  return fetchHexaRateRateToEuroAtDateRaw(currencyCodeInput, contractDateInput);
}

export type CurrentRateInfoResolved = {
  rateToEuro: number;
  currencyCode: string;
  currencySymbol: string;
  ratedAt: string | null;
  source: "api_today" | "local_fallback";
};

export async function resolveCurrentRateInfoWithTodayApi(
  deviseInput: any,
): Promise<CurrentRateInfoResolved | null> {
  if (!deviseInput) return null;

  const currencyCode = String(deviseInput?.code ?? "")
    .trim()
    .toUpperCase();
  if (!currencyCode || currencyCode === "EUR") return null;

  const localRateInfo = getCurrentRateInfo(deviseInput);
  const currencySymbol =
    localRateInfo?.currencySymbol ||
    deviseInput?.symbol ||
    deviseInput?.symbolNative ||
    currencyCode;

  const todayResult = await fetchHexaRateRateToEuroAtDateRaw(currencyCode, new Date());
  if (
    todayResult.status === "ok" &&
    typeof todayResult.rateToEuro === "number" &&
    todayResult.rateToEuro > 0
  ) {
    return {
      rateToEuro: todayResult.rateToEuro,
      currencyCode,
      currencySymbol,
      ratedAt: todayResult.dateUsed,
      source: "api_today",
    };
  }

  if (
    localRateInfo &&
    typeof localRateInfo.rateToEuro === "number" &&
    localRateInfo.rateToEuro > 0
  ) {
    return {
      rateToEuro: localRateInfo.rateToEuro,
      currencyCode: String(localRateInfo.currencyCode ?? currencyCode),
      currencySymbol:
        localRateInfo.currencySymbol || currencySymbol || localRateInfo.currencyCode,
      ratedAt: localRateInfo.ratedAt ?? null,
      source: "local_fallback",
    };
  }

  return null;
}

export async function resolveRealFundingContractRateToEuro(
  funding: any,
  projectCurrencyCodeInput?: any,
): Promise<ResolvedRateToEuro> {
  const currencyCode =
    String(projectCurrencyCodeInput ?? "")
      .trim()
      .toUpperCase() || undefined;

  return resolveHistoricalRateToEuroAtDate(
    currencyCode,
    readRealFundingContractDateRaw(funding),
    "contract_date",
  );
}

export async function resolveProjectBudgetRateToEuro(
  project: any,
): Promise<ResolvedRateToEuro> {
  const currencyCode = readProjectCurrencyCode(project);
  if (!currencyCode) {
    return { rateToEuro: null, dateUsed: null, source: "unavailable" };
  }

  if (currencyCode === "EUR") {
    return { rateToEuro: 1, dateUsed: null, source: "eur" };
  }

  const launchDateRaw = readProjectLaunchDateRaw(project);
  const launchRateResult = await fetchHexaRateRateToEuroAtDateRaw(
    currencyCode,
    launchDateRaw,
  );
  if (launchRateResult.status === "ok") {
    return {
      rateToEuro: launchRateResult.rateToEuro,
      dateUsed: launchRateResult.dateUsed,
      source: "launch_date",
    };
  }

  const today = new Date();
  const currentApiRateResult = await fetchHexaRateRateToEuroAtDateRaw(
    currencyCode,
    today,
  );
  if (currentApiRateResult.status === "ok") {
    return {
      rateToEuro: currentApiRateResult.rateToEuro,
      dateUsed: currentApiRateResult.dateUsed,
      source: "current_fallback",
    };
  }

  const currentRateToEuro = readProjectCurrentRateToEuro(project);
  if (typeof currentRateToEuro === "number" && currentRateToEuro > 0) {
    return {
      rateToEuro: currentRateToEuro,
      dateUsed: readProjectCurrentRateDate(project),
      source: "current_fallback",
    };
  }

  return {
    rateToEuro: null,
    dateUsed: launchRateResult.dateUsed,
    source: "unavailable",
  };
}
